import { ChangeEvent, useEffect, useState } from "react";
import { EventAutocompleteOption } from "../../../globalSettings/types";
import { useEventSelect } from "../../../globalSettings/hooks/useEventSelect";
import { ODIN_EVENT_EDITION_STORAGE_KEY } from "../../../../common/constants";

type Props = {
  options: EventAutocompleteOption[];
  fetchEventEdition?: (eventEditionId: string) => Promise<void>;
  fetchCounties?: () => Promise<void>;
}

export const useEventDropdown = ({ options, fetchEventEdition, fetchCounties }: Props) => {
  const sortFilteredOptions = (event1: EventAutocompleteOption, event2: EventAutocompleteOption) => event1.gbsCode.localeCompare(event2.gbsCode);

  const [selectedOption, setSelectedOption] = useState<EventAutocompleteOption>();
  const [isSubmitting, setSubmitting] = useState(false);
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const { onEventChange } = useEventSelect(true);
  const [filteredOptions, setFilteredOptions] = useState(options.sort(sortFilteredOptions));


  const onClick = async () => {
    if (selectedOption && fetchEventEdition && fetchCounties) {
      setSubmitting(true);
      await fetchEventEdition(selectedOption.atlasId);
      await fetchCounties();
      setSubmitting(false);
    }
  };

  const onDropdownClick = () => {
    setIsButtonClicked(!isButtonClicked);
    if (!isButtonClicked) {
      setFilteredOptions(options.sort(sortFilteredOptions));
    }
  }

  const onChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const valueEntered = event.target.value.toLowerCase();
    setFilteredOptions(event.target.value
      ? options.filter(({ label, gbsCode }) => label.toLowerCase().includes(valueEntered) ||
        gbsCode.toLowerCase().includes(valueEntered)).sort(sortFilteredOptions)
      : options.sort(sortFilteredOptions)
    );
  }

  const onSelect = (eventEditionId: string) => {
    setSelectedOption(options.find(i => i.atlasId === eventEditionId));
    setIsButtonClicked(false);
  }

  const selectAndRefresh = (eventEditionId: string) => {
    const event = options.find(event => event.atlasId === eventEditionId);
    setSubmitting(event?.atlasId !== selectedOption?.atlasId);
    setIsButtonClicked(false);
    onEventChange(event!);
  }

  const preselectEvent = () => {
    const fromStorage = localStorage.getItem(ODIN_EVENT_EDITION_STORAGE_KEY);
    if (!fromStorage) {
      return;
    }
    setSelectedOption(options.find(option => option.atlasId === fromStorage));
  }

  useEffect(() => {
    preselectEvent();
  }, []);

  const onClickAway = () => {
    setIsButtonClicked(false);
  }

  return {
    selectedOption,
    isSubmitting,
    isButtonClicked,
    filteredOptions,
    onDropdownClick,
    onClick,
    onChange,
    onSelect,
    selectAndRefresh,
    onClickAway
  };
};
